import { useMemo } from 'react';
import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import Display from '@/components/typography/Display';
import Button from '@/components/ui/Button';
import { Container, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath } from '@/lib/locale';
import Seo from '@/components/Seo';
import { BASE_URL } from '@/seo/meta';

export default function ChecklistThankYouPage() {
  const { language, t } = useLanguage();
  const localizedPath = useLocalizedPath();
  const isGreek = language === 'el';

  const seoMeta = useMemo(() => {
    if (isGreek) {
      return {
        title: 'Ευχαριστούμε | PixelPro Solutions',
        description: 'Κατέβασε το δωρεάν PDF checklist για την επαγγελματική σου ιστοσελίδα.',
        canonical: `${BASE_URL}/thank-you/checklist`,
      };
    }
    return {
      title: 'Thank You | PixelPro Solutions',
      description: 'Download the free PDF checklist for your professional website.',
      canonical: `${BASE_URL}/en/thank-you/checklist`,
    };
  }, [isGreek]);

  return (
    <>
      <Seo
        title={seoMeta.title}
        description={seoMeta.description}
        url={seoMeta.canonical}
        noindex
      />

      <div className="relative min-h-screen bg-[#080c0d] text-white">
        <PixelBackground />
        <Header variant="simple" />

        <main id="main-content" className="relative z-10">
          <Section className="relative overflow-hidden pt-28 lg:pt-32">
            <div className="absolute inset-0 geometric-pattern" aria-hidden="true" />
            <div className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black" aria-hidden="true" />
            <Container className="relative max-w-4xl">
              <div className="rounded-[32px] border border-white/15 bg-black/70 backdrop-blur-xl p-8 md:p-12 shadow-glass text-center relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-highlight/10 via-transparent to-white/5 pointer-events-none" aria-hidden="true" />
                <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-brand-highlight/20 blur-3xl" aria-hidden="true" />
                <div className="pointer-events-none absolute -left-24 bottom-0 h-56 w-56 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
                <div className="relative">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.4em] text-brand-highlight/80 mb-4">
                    {t('checklist.thankYou.eyebrow')}
                  </p>
                  <Display
                    variant="h1"
                    as="h1"
                    className="text-white text-3xl md:text-4xl lg:text-5xl leading-[1.15] mb-4"
                  >
                    {t('checklist.thankYou.title')}
                  </Display>
                  <p className="text-white/70 text-lg mb-8">
                    {t('checklist.thankYou.subtitle')}
                  </p>

                  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button
                      as="a"
                      href="/website-checklist-pixelpro-solutions.pdf"
                      variant="primary"
                      size="lg"
                      className="justify-center"
                      download
                    >
                      {t('checklist.thankYou.download')}
                    </Button>
                    <Button
                      as="a"
                      href={localizedPath('/consultation')}
                      variant="secondary"
                      size="lg"
                      className="justify-center"
                    >
                      {t('checklist.thankYou.secondaryCta')}
                    </Button>
                  </div>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </>
  );
}
